import { AlignJustify, List } from 'lucide-react'
import type { OutlineStyle } from '@/features/projects/use-outline-style'

interface OutlineStyleToggleProps {
  style: OutlineStyle
  onChange: (next: OutlineStyle) => void
}

/**
 * Two-way segmented control for swapping the Project viewer's outline density. Sits in the
 * scaffold toolbar alongside other mode controls.
 */
export const OutlineStyleToggle = ({ style, onChange }: OutlineStyleToggleProps) => (
  <div
    role="radiogroup"
    aria-label="Outline density"
    className="inline-flex items-center rounded-md border bg-background p-0.5 text-sm"
  >
    <OutlineStyleButton
      label="Detailed"
      active={style === 'detailed'}
      onClick={() => onChange('detailed')}
      icon={<AlignJustify className="h-4 w-4" aria-hidden />}
    />
    <OutlineStyleButton
      label="Basic"
      active={style === 'basic'}
      onClick={() => onChange('basic')}
      icon={<List className="h-4 w-4" aria-hidden />}
    />
  </div>
)

interface OutlineStyleButtonProps {
  label: string
  active: boolean
  onClick: () => void
  icon: React.ReactNode
}

const OutlineStyleButton = ({ label, active, onClick, icon }: OutlineStyleButtonProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
    className={
      'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 transition-colors ' +
      (active
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:text-foreground')
    }
  >
    {icon}
    <span>{label}</span>
  </button>
)
